<template>
  <div class="min-h-screen bg-background">
    <PageHeader />

    <main>
      <!-- Top Banner -->
      <div class="bg-[#7c2622] py-16">
        <div class="mx-auto max-w-[1400px] px-8 lg:px-12">
          <h1 class="font-serif text-3xl lg:text-4xl text-white">
            Hidden Selection
          </h1>
          <p class="text-white/70 mt-2">
            Excellence discovered, not declared.
          </p>
        </div>
      </div>

      <!-- Judging Criteria -->
      <section class="py-20 lg:py-28 bg-background">
        <div class="mx-auto max-w-[1400px] px-8 lg:px-12">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">

            <!-- Left -->
            <div>
              <div class="mb-12 animate-fade-in">
                <p class="text-sm font-medium tracking-widest uppercase text-gray-500 mb-4">
                  Evaluation Process
                </p>

                <h2 class="font-serif text-3xl lg:text-4xl text-foreground mb-4">
                  A Selection You Never See Coming
                </h2>

                <p class="text-gray-600">
                  Unlike traditional awards, candidates do not apply.
                  Instead, our committee identifies and evaluates potential
                  recipients through a fully concealed process — ensuring that
                  only genuine excellence is recognised.
                </p>
              </div>

              <div class="space-y-6">
                <div
                  v-for="(item, index) in criteria"
                  :key="item.title"
                  class="border-l-2 border-[#7c2622]/30 pl-6 animate-fade-in"
                  :style="{ animationDelay: index * 0.1 + 's' }"
                >
                  <h3 class="font-medium text-foreground mb-1">
                    {{ item.title }}
                  </h3>
                  <p class="text-sm text-gray-600">
                    {{ item.description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Right -->
            <div
              class="bg-gray-50 p-8 lg:p-12 animate-fade-in"
              :style="{ animationDelay: '0.2s' }"
            >
              <h3 class="font-serif text-2xl text-foreground mb-6">
                Evaluation Stages
              </h3>

              <div class="space-y-8">
                <div
                  v-for="stage in stages"
                  :key="stage.num"
                  class="flex items-start gap-4"
                >
                  <div
                    class="flex-shrink-0 h-8 w-8 bg-[#7c2622] text-white flex items-center justify-center text-sm font-medium"
                  >
                    {{ stage.num }}
                  </div>

                  <div>
                    <h4 class="font-medium text-foreground mb-1">
                      {{ stage.title }}
                    </h4>
                    <p class="text-sm text-gray-600">
                      {{ stage.desc }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <!-- Judging Panel -->
      <section class="py-20 lg:py-28 bg-gray-50">
        <div class="mx-auto max-w-[1400px] px-8 lg:px-12">

          <div class="max-w-2xl mb-16 animate-fade-in">
            <p class="text-sm font-medium tracking-widest uppercase text-gray-500 mb-4">
              Judging Panel
            </p>

            <h2 class="font-serif text-3xl lg:text-4xl text-foreground mb-4">
              Anonymous Evaluation Committee
            </h2>

            <p class="text-gray-600">
              The identities of all panel members are kept strictly confidential
              to safeguard the integrity of the selection process.
              Below are their roles and general credentials only.
            </p>
          </div>

          <!-- <div class="grid md:grid-cols-2 gap-6">
            <div
              v-for="(member, index) in panelMembers"
              :key="index"
              class="bg-white border border-gray-200 p-8 animate-fade-in hover:border-[#7c2622]/40 transition-colors"
              :style="{ animationDelay: index * 0.1 + 's' }"
            >
              <div class="flex gap-6">
                <div class="flex-shrink-0 h-16 w-16 bg-gray-200"></div>

                <div>
                  <h3 class="font-serif text-lg text-foreground mb-1 italic">
                    {{ member.name }}
                  </h3>
                  <p class="text-sm font-medium text-gray-500 mb-3">
                    {{ member.title }}
                  </p>
                  <p class="text-sm text-gray-600 leading-relaxed">
                    {{ member.credentials }}
                  </p>
                </div>
              </div>
            </div>
          </div> -->

          <div
            class="mt-12 p-8 bg-white border border-gray-200 animate-fade-in"
            :style="{ animationDelay: '0.4s' }"
            >
            <h3 class="font-serif text-xl text-foreground mb-6">
                Organising Body
            </h3>

            <p class="text-gray-600 leading-relaxed mb-4">
                The Souvenir Excellence Awards is organised and administered by the 
                Souvenir & Cultural Retail Association, an established industry body 
                committed to advancing quality, innovation, and professionalism within 
                the souvenir and cultural retail sector. The Association serves as a 
                collective platform representing retailers, manufacturers, designers, 
                and cultural enterprises dedicated to strengthening the industry’s 
                standards and global competitiveness.
            </p>

            <p class="text-gray-600 leading-relaxed mb-4">
                With a clear mandate to elevate industry benchmarks, the Association 
                works closely with stakeholders across the value chain to promote 
                ethical practices, responsible sourcing, product excellence, and 
                sustainable business growth. Through structured initiatives, industry 
                dialogues, and recognition platforms such as the Souvenir Excellence 
                Awards, it aims to encourage continuous improvement and long-term 
                value creation.
            </p>

            <p class="text-gray-600 leading-relaxed mb-4">
                The Awards programme is governed by a transparent and independent 
                framework designed to ensure credibility at every stage from 
                nomination and evaluation to final selection. Clear criteria, 
                documented assessment processes, and oversight mechanisms are 
                implemented to safeguard fairness and impartiality.
            </p>

            <p class="text-sm text-gray-600">
                Upholding the principles of integrity, accountability, and transparency, 
                the Association remains dedicated to recognising outstanding 
                contributions within the sector while fostering trust and confidence 
                across the wider industry ecosystem.
            </p>
        </div>
        </div>
      </section>

    </main>

    <PageFooter />
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue"
import PageFooter from "@/components/PageFooter.vue"

export default {
  name: "Judging",
  components: {
    PageHeader,
    PageFooter,
  },
  data() {
    return {
      criteria: [
        {
          title: "Undisclosed Evaluation Metrics",
          description:
            "Recipients are assessed against a confidential set of criteria known only to the selection committee, ensuring unbiased and authentic results.",
        },
        {
          title: "Anonymous Field Observation",
          description:
            "Our evaluators conduct covert visits and assessments without prior notice, capturing genuine day-to-day operations and customer interactions.",
        },
        {
          title: "Sealed Deliberation Process",
          description:
            "All panel discussions and scoring remain strictly confidential. No candidate is informed of their nomination status until final announcement.",
        },
        {
          title: "Integrity Safeguards",
          description:
            "Multiple layers of anonymity protect both evaluators and candidates, eliminating any possibility of influence or bias in the outcome.",
        },
      ],
      stages: [
        { num: 1, title: "Silent Nomination", desc: "Potential recipients are secretly nominated by industry insiders, past winners, and our network of scouts." },
        { num: 2, title: "Covert Observation", desc: "Trained evaluators visit businesses anonymously, assessing operations without the candidate's knowledge." },
        { num: 3, title: "Confidential Scoring", desc: "All findings are scored behind closed doors using sealed criteria that remain undisclosed to the public." },
        { num: 4, title: "Surprise Announcement", desc: "Winners are revealed only at the official ceremony — the first time they learn of their selection." },
      ],
      panelMembers: [
        {
          name: "Undisclosed",
          title: "Panel Chair",
          credentials:
            "A senior figure in tourism governance with over two decades of experience in destination development and cultural policy.",
        },
        {
          name: "Undisclosed",
          title: "Retail Sector Evaluator",
          credentials:
            "A respected authority in retail operations, merchandising standards, and consumer experience assessment.",
        },
        {
          name: "Undisclosed",
          title: "Cultural Heritage Specialist",
          credentials:
            "An established academic and practitioner in material culture, ethnographic studies, and heritage preservation.",
        },
        {
          name: "Undisclosed",
          title: "Artisan Craft Assessor",
          credentials:
            "A recognised master craftsperson with international accreditation in traditional craftsmanship and artisan quality standards.",
        },
      ],
    }
  },
}
</script>